import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

interface IListParamDefaults {
  search: string;
  filter: string;
  page: number;
}

interface IUseListParamsReturn {
  search: string;
  filter: string;
  page: number;
  setSearch: (value: string) => void;
  setFilter: (value: string) => void;
  setPage: (page: number) => void;
  reset: () => void;
}

const useListParams = (
  defaults?: Partial<IListParamDefaults>,
): IUseListParamsReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultSearch = defaults?.search ?? '';
  const defaultFilter = defaults?.filter ?? '';
  const defaultPage = defaults?.page ?? 1;

  const search = searchParams.get('search') ?? defaultSearch;
  const filter = searchParams.get('filter') ?? defaultFilter;
  const page = Number(searchParams.get('page')) || defaultPage;

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value && value !== defaultSearch) {
          next.set('search', value);
        } else {
          next.delete('search');
        }

        // Reset page to 1 when search changes
        next.delete('page');

        return next;
      });
    },
    [setSearchParams, defaultSearch],
  );

  const setFilter = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value && value !== defaultFilter) {
          next.set('filter', value);
        } else {
          next.delete('filter');
        }

        // Reset page to 1 when filter changes
        next.delete('page');

        return next;
      });
    },
    [setSearchParams, defaultFilter],
  );

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (newPage > 1) {
          next.set('page', String(newPage));
        } else {
          next.delete('page');
        }

        return next;
      });
    },
    [setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('search');
      next.delete('filter');
      next.delete('page');

      return next;
    });
  }, [setSearchParams]);

  return {
    search,
    filter,
    page,
    setSearch,
    setFilter,
    setPage,
    reset,
  };
};

export default useListParams;
